import Cookies from 'js-cookie'
import getConfig from 'next/config'
import { useCallback, useEffect, useState } from 'react'
import { hasValidAccessToken } from 'services/cognito/refresh-token'
import { FetchMethods } from './types'
import { toggleLoader } from './spinner-helper'
import { useAppErrorApiContext } from 'context/AppErrorContext'

function fetchPath(path: string) {
    const { API_ENDPOINT } = getConfig().publicRuntimeConfig
    return (API_ENDPOINT || '/api') + path
}

function buildRequestHeaders(headers: HeadersInit = {}): HeadersInit {
    let request_headers: HeadersInit = {
        ...headers,
        'Content-Type': 'application/json',
    }
    const Authorization = getAuthHeaderToken()
    if (Authorization)
        request_headers = {
            ...request_headers,
            Authorization,
        }

    return request_headers
}

function getAuthHeaderToken() {
    if (Cookies.get('id_token')) {
        return `Bearer ${Cookies.get('id_token')}`
    }
    return ''
}

function checkStatusCode(status_code: number) {
    if (status_code >= 500) {
        let error_count =
            (sessionStorage.getItem('error_count') as unknown as number) ||
            0
        error_count++
        sessionStorage.setItem(
            'error_count',
            error_count as unknown as string
        )
        if (error_count >= 3) {
            console.error('Encountered more 500 errors. We are in maintenance mode.')
            // window.location.href = '/maintenance.html'
        }
    } else {
        sessionStorage.setItem('error_count', '0')
    }
}

async function handleExpiredTenant(response: any) {
    let status_code = response.status
    if (status_code == 401) {
        let response_json = await response.json()
        if (response_json.tag == 'entitlement.error.tenant_has_no_entitlement' 
            && window.location.href.indexOf('/dashboard/profile/') == -1) {
            console.error(response_json.message)
            window.location.href = '/dashboard/profile/?upsell=true'
        }
    }
}

export async function getApiRequest(
    path: string,
    headers: Record<string, string> = {},
    expects_text?: boolean
) {
    if (!hasValidAccessToken()) {
        return { error: 'Invalid Access Token' }
    }

    let req_init: RequestInit = {
        method: FetchMethods.GET,
        mode: 'cors',
        headers: buildRequestHeaders(headers),
    }

    try {
        const response = await fetch(fetchPath(path), req_init)

        checkStatusCode(response.status)
        handleExpiredTenant(response)

        if (response.status > 402) {
            return {
                status: response.status,
                ok: response.ok,
                error: 'API error',
            }
        }
        try {
            const content_type = headers?.['Content-Type']
            if (expects_text || content_type === 'text')
                return await response.text()
            return await response.json()
        } catch (e) {
            return { error: 'API error' }
        }
    } catch (e) {
        console.error('getApiRequest:', (e as unknown as Error).message)
    }
}

export async function postApiRequest(
    path: string,
    body: unknown = {},
    headers: Record<string, string> = {},
    expects_nothing?: boolean
) {
    if (!hasValidAccessToken()) {
        return { error: 'Invalid Access Token' }
    }

    let req_init: RequestInit = {
        body: JSON.stringify(body),
        method: FetchMethods.POST,
        mode: 'cors',
        headers: buildRequestHeaders(headers),
    }

    try {
        const response = await fetch(fetchPath(path), req_init)

        checkStatusCode(response.status)
        handleExpiredTenant(response)

        try {
            if (expects_nothing) {
                return response
            }
            const results = await response.json()
            results.status = response.status
            results.ok = response.ok
            return results
        } catch (e) {
            return { error: 'API error ' }
        }
    } catch (e) {
        console.error('postApiRequest:', (e as unknown as Error).message)
    }
}

export async function putApiRequest(
    path: string,
    body: unknown = {},
    headers: Record<string, string> = {}
) {
    if (!hasValidAccessToken()) {
        return { error: 'Invalid Access Token' }
    }

    let req_init: RequestInit = {
        body: JSON.stringify(body),
        method: FetchMethods.PUT,
        mode: 'cors',
        headers: buildRequestHeaders(headers),
    }

    try {
        const response = await fetch(fetchPath(path), req_init)

        checkStatusCode(response.status)
        handleExpiredTenant(response)

        try {
            const results = await response.json()
            results.status = response.status
            results.ok = response.ok
            return results
        } catch (e) {
            return { error: 'API error' }
        }
    } catch (e) {
        console.error('putApiRequest:', (e as unknown as Error).message)
    }
}

export async function patchApiRequest(
    path: string,
    body: unknown = {},
    headers: Record<string, string> = {}
) {
    if (!hasValidAccessToken()) {
        return { error: 'Invalid Access Token' }
    }

    let req_init: RequestInit = {
        body: JSON.stringify(body),
        method: FetchMethods.PATCH,
        mode: 'cors',
        headers: buildRequestHeaders(headers),
    }

    try {
        const response = await fetch(fetchPath(path), req_init)

        checkStatusCode(response.status)
        handleExpiredTenant(response)

        try {
            const results = await response.json()
            results.status = response.status
            results.ok = response.ok
            return results
        } catch (e) {
            return { error: 'API error' }
        }
    } catch (e) {
        console.error('patchApiRequest:', (e as unknown as Error).message)
    }
}

export async function deleteApiRequest(
    path: string,
    headers: Record<string, string> = {},
    body?: unknown
) {
    if (!hasValidAccessToken()) {
        return { error: 'Invalid Access Token' }
    }

    let req_init: RequestInit = {
        method: FetchMethods.DELETE,
        mode: 'cors',
        headers: buildRequestHeaders(headers),
    }

    if (body) {
        req_init.body = JSON.stringify(body) as BodyInit
    }

    try {
        const response = await fetch(fetchPath(path), req_init)

        checkStatusCode(response.status)
        handleExpiredTenant(response)

        const ok = response.ok
        try {
            return {
                ...(await response.json()),
                ok,
            }
        } catch (e) {
            return { error: 'API response parsing error', e, ok }
        }
    } catch (e) {
        console.error('deleteApiRequest:', (e as unknown as Error).message)
    }
}

export function useFetch<Body = any, Result = any>(
    path: string,
    method: FetchMethods = FetchMethods.GET,
    fetch_on_mount: boolean = true,
    expects_json: boolean = true
) {
    const { setGlobalError } = useAppErrorApiContext()
    const [is_loading, setLoading] = useState(fetch_on_mount)
    const [data, setData] = useState<Result>({} as Result)
    const [status, setStatus] = useState(0)

    const doFetch = useCallback(
        async (body?: Body, query: string = '', headers = {}) => {
            if (!hasValidAccessToken()) {
                console.error('Invalid Access Token')
                setStatus(400)
            }

            let req_init: RequestInit = {
                method,
                mode: 'cors',
                headers: buildRequestHeaders(headers),
            }
            if (body) {
                req_init = {
                    ...req_init,
                    body: JSON.stringify(body),
                }
            }

            setLoading(true)
            toggleLoader(true, method != FetchMethods.GET)

            try {
                const response = await fetch(
                    fetchPath(path) + query,
                    req_init
                )

                checkStatusCode(response.status)
                handleExpiredTenant(response)

                if (expects_json) {
                    try {
                        setData((await response.json()) as Result)
                    } catch (e) {
                        setData({ error: 'API error' } as unknown as Result)
                    }
                } else {
                    setData({
                        response_text: await response.text(),
                    } as unknown as Result)
                }

                setStatus(response.status)

                return response
            } catch (e) {
                if (path == '/v1/tiers') {
                    console.error('Tiers API failed. We are in maintenace mode.')
                    window.location.href = '/maintenance.html'
                }
                handleExpiredTenant(e)
                setGlobalError(e)
                console.error('doFetch:', (e as unknown as Error).message)
                // if (e.message && e.message.indexOf('Failed to fetch')) {
                //     window.location.href = '/maintenance.html'
                // }
            } finally {
                setLoading(false)
                toggleLoader(false, method != FetchMethods.GET)
            }
        },
        [method, path, expects_json]
    )

    useEffect(() => {
        fetch_on_mount && doFetch()
    }, [doFetch, fetch_on_mount])

    return {
        is_loading,
        data,
        status,
        doFetch,
        setData,
    }
}

export function useApiRequest() {
    const getApiRequest = async function (
        path: string,
        headers: Record<string, string> = {},
        expects_text?: boolean,
        show_blocking = false
    ) {
        if (!hasValidAccessToken()) {
            return { error: 'Invalid Access Token' }
        }

        let req_init: RequestInit = {
            method: FetchMethods.GET,
            mode: 'cors',
            headers: buildRequestHeaders(headers),
        }

        try {
            toggleLoader(true)
            const response = await fetch(fetchPath(path), req_init)

            checkStatusCode(response.status)
            handleExpiredTenant(response)

            if (response.status > 402) {
                return {
                    status: response.status,
                    ok: response.ok,
                    error: 'API error',
                }
            }
            try {
                const content_type = headers?.['Content-Type']
                if (expects_text || content_type === 'text')
                    return await response.text()
                return await response.json()
            } catch (e) {
                if (e.toString().indexOf('Unexpected end of JSON input') > -1) {
                    return {}
                }
                throw { message: e }
            }
        } catch (e) {
            console.error('getApiRequest:', (e as unknown as Error).message)
            return { error: 'API error' }
        } finally {
            toggleLoader(false)
        }
    }

    return {
        getApiRequest
    }
}